import { medicalCheckupsService } from './medicalCheckupsService';
import { medicsService } from './medicsService';
import { cryptoProService } from './cryptoProService';
import type { Certificate } from '../models/digitalSignatures/cryptoProTypes';
import { MedicalCheckupVerdictDto } from '../models/medicalCheckups/medicalCheckupVerdictDto';
import { MedicalCheckupVerdictWithCloudTokenDto } from '../models/medicalCheckups/medicalCheckupVerdictWithCloudTokenDto';
import { TokenType } from '../models/enums/tokenType';
import { MedicDigitalSignDto } from '../models/medics/medicDigitalSignDto';
import { SigningStep, SigningStepUtil } from '../models/enums/signingStep';
import { SigningContext } from '../models/enums/signingContext';

export interface SigningProgress {
    step: SigningStep;
    message: string;
}

export interface SigningResult {
    success: boolean;
    requiresCertificateSelection?: boolean;
    error?: string;
}

export interface SigningOptions {
    onProgress?: (progress: SigningProgress) => void;
}

/**
 * Класс для ошибок валидации с бэка
 */
export class ValidationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ValidationError';
    }
}

/**
 * Класс для ошибок настройки рабочего места
 */
export class WorkplaceError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'WorkplaceError';
    }
}

/**
 * Класс для сетевых ошибок (нет ответа от сервера)
 */
export class NetworkError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'NetworkError';
    }
}

export const NETWORK_ERROR_MESSAGE = 'Сетевая ошибка. Проверьте подключение к интернету и попробуйте снова.';

export function isNetworkError(error: any): boolean {
    return !error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error');
}

class MedicalCheckupSigningService {
    /**
     * Уведомление о прогрессе подписания
     */
    private notifyProgress(
        onProgress: ((progress: SigningProgress) => void) | undefined,
        step: SigningStep,
        context?: SigningContext
    ): void {
        onProgress?.({
            step,
            message: SigningStepUtil.getMessage(step, context)
        });
    }

    /**
     * Подписание медосмотра с автоматическим определением типа подписания
     * @param checkupId - ID медосмотра
     * @param verdictData - Данные вердикта
     * @param options - Опции подписания (callback для прогресса)
     * @returns Результат подписания
     */
    async signMedicalCheckup(
        checkupId: number,
        verdictData: MedicalCheckupVerdictDto,
        options: SigningOptions = {}
    ): Promise<SigningResult> {
        const { onProgress } = options;

        try {
            // Проверяем тип сертификата медика (дефолтный сертификат из БД)
            this.notifyProgress(onProgress, SigningStep.CheckingCertificate);

            const defaultCert = await medicsService.getMedicCertificate();

            if (!defaultCert) {
                // Если дефолтный сертификат не найден, требуется выбор сертификата
                return {
                    success: false,
                    requiresCertificateSelection: true
                };
            }

            // Если есть сертификат Cloud, подписываем через Signer Service
            if (defaultCert.tokenType === TokenType.Cloud) {
                await this.signWithCloudCertificate(checkupId, verdictData, onProgress);
                return { success: true };
            }

            // Если это Physical токен, подписываем через физический токен
            if (defaultCert.tokenType === TokenType.Physical) {
                await this.signWithPhysicalToken(
                    checkupId,
                    verdictData,
                    defaultCert.thumbprint,
                    defaultCert,
                    onProgress
                );
                return { success: true };
            }

            // Неизвестный тип токена - требуется выбор сертификата
            return {
                success: false,
                requiresCertificateSelection: true
            };
        } catch (error: any) {
            // Ошибки уже нормализованы в signWithPhysicalToken и signWithCloudCertificate
            // Просто пробрасываем их дальше
            throw error;
        }
    }

    /**
     * Подписание медосмотра с выбранным сертификатом
     * @param checkupId - ID медосмотра
     * @param verdictData - Данные вердикта
     * @param certificate - Выбранный сертификат
     * @param options - Опции подписания
     * @returns Результат подписания
     */
    async signWithSelectedCertificate(
        checkupId: number,
        verdictData: MedicalCheckupVerdictDto,
        certificate: Certificate,
        options: SigningOptions = {}
    ): Promise<void> {
        const { onProgress } = options;

        await this.signWithPhysicalToken(
            checkupId,
            verdictData,
            certificate.thumbprint,
            null, // Не сохраненный сертификат
            onProgress
        );
    }

    /**
     * Подписание через облачный сертификат (Signer Service)
     */
    async signWithCloudCertificate(
        checkupId: number,
        verdictData: MedicalCheckupVerdictDto,
        onProgress?: (progress: SigningProgress) => void
    ): Promise<void> {
        try {
            this.notifyProgress(onProgress, SigningStep.Sending, SigningContext.SignerService);

            // Преобразуем в DTO для облачного токена (убираем поле signature)
            const cloudTokenVerdict: MedicalCheckupVerdictWithCloudTokenDto = {
                result: verdictData.result,
                rejectReasons: verdictData.rejectReasons,
                comment: verdictData.comment,
            };

            await medicalCheckupsService.setVerdictWithCloudToken(checkupId, cloudTokenVerdict);

            this.notifyProgress(onProgress, SigningStep.Completed);
        } catch (error: any) {
            // Если ошибка пришла с API (есть response) - это ошибка валидации с бэка
            if (error.response) {
                const errorMessage = error.response?.data?.message ?? error.message ?? 'Ошибка валидации';
                throw new ValidationError(errorMessage);
            }
            // Сетевая ошибка — показываем отдельную модалку с русским текстом
            if (isNetworkError(error)) {
                throw new NetworkError(NETWORK_ERROR_MESSAGE);
            }
            throw error;
        }
    }

    /**
     * Подписание через физический токен
     */
    async signWithPhysicalToken(
        checkupId: number,
        verdictData: MedicalCheckupVerdictDto,
        thumbprint: string,
        defaultCert: MedicDigitalSignDto | null,
        onProgress?: (progress: SigningProgress) => void
    ): Promise<void> {
        try {
            // 1. Проверяем готовность рабочего места (плагин, CSP, наличие USB-ключа и сертификатов)
            const workplaceStatus = await cryptoProService.checkWorkplaceReadiness();
            if (!workplaceStatus.isReady) {
                throw new Error(workplaceStatus.errors || 'Рабочее место не готово к подписанию медосмотра');
            }

            const onToken = await cryptoProService.isCertificateAvailableOnToken(thumbprint);
            if (!onToken) {
                throw new Error('Не найден ключ для подписания');
            }

            // 2. Получаем JSON и хеш документа с бэкенда (эндпоинт /hash, JSON уже сохраняется в S3)
            this.notifyProgress(onProgress, SigningStep.GettingHash);
            const { json, hash } = await medicalCheckupsService.createHash(checkupId, verdictData);
            
            // 3. Подписываем JSON на стороне клиента с использованием CryptoPro
            this.notifyProgress(onProgress, SigningStep.Signing);
            const signature = await cryptoProService.sign(
                thumbprint,
                btoa(json), // Передаем данные в base64 для подписания
                {
                    detached: true,
                    addUserCertificate: true,
                }
            );

            // 4. Отправляем вердикт с подписью и хешем на бэкенд.
            //    result, rejectReasons и comment уже сохранены в медосмотр при создании хеша через /hash
            this.notifyProgress(onProgress, SigningStep.Sending);
            await medicalCheckupsService.setVerdictWithPhysicalToken(checkupId, {
                signature: signature,
                hash: hash,
            });

            this.notifyProgress(onProgress, SigningStep.Completed);
        } catch (error: any) {
            // Если ошибка пришла с API (есть response) - это ошибка валидации с бэка
            if (error.response) {
                const errorMessage = error.response?.data?.message ?? error.message ?? 'Ошибка валидации';
                throw new ValidationError(errorMessage);
            }
            // Сетевая ошибка — показываем отдельную модалку с русским текстом
            if (isNetworkError(error)) {
                throw new NetworkError(NETWORK_ERROR_MESSAGE);
            }
            // Локальная ошибка (настройка рабочего места, CryptoPro и т.д.)
            const normalizedError = cryptoProService.normalizeError(error, 'медосмотра');
            throw new WorkplaceError(normalizedError);
        }
    }
}

export const medicalCheckupSigningService = new MedicalCheckupSigningService();

